/* @flow */

/*
 * various helper functions
 *
 * use multiple files if this gets too large
 */

import {
  keyBy,
  isEmpty,
  merge,
  omit,
  includes,
  without,
  concat,
  chain,
} from 'lodash'
import ReactDOM from 'react-dom'
import { call, put, select } from 'redux-saga/effects'

import type { Vector } from './types'

import { goToLogin, goToOldLogin } from './stores/routing'
import { setAlertDanger, setAlertDangerNotAllowed } from './stores/alert'
import { AUTH_COOKIE, AUTH_TOKEN, isToolUser } from './stores/usersStore'
import { selectCurrentUser } from './stores/currentUser'

export function objectToQueryString(object: Object): string {
  return Object.keys(object)
    .map(key => {
      return [key, object[key]].map(encodeURIComponent).join('=')
    })
    .join('&')
}

export function addQueryToUrl(url: string, query: ?Object): string {
  if (query == null) {
    return url
  }

  return `${url}?${objectToQueryString(query)}`
}

export function deepcopy(o: Object) {
  return JSON.parse(JSON.stringify(o))
}

/*
 * insert or update one or many items
 */
export function upsert<T>(repository: Object, items: ?Array<T>) {
  const keyedById = keyBy(items, 'id')

  // this is a performance optimization
  if (isEmpty(repository)) {
    return keyedById
  }

  return Object.assign({}, repository, keyedById)
}

export function insertOrMerge<T>(
  repository: Object,
  items: ?Array<T>,
  key: string = 'id'
) {
  const keyedById = keyBy(items, key)

  // this is a performance optimization
  if (isEmpty(repository)) {
    return keyedById
  }

  return merge({}, repository, keyedById)
}

export function mergeArrayByKey(
  arr1: Array<Object>,
  arr2: Array<Object>,
  key: string = 'id',
  customizer: ?Function
) {
  return chain(arr1)
    .keyBy(key)
    .mergeWith(keyBy(arr2, key), customizer)
    .values()
    .value()
}

export function addNumbersCustomizer(objValue: any, srcValue: any) {
  if (typeof objValue === 'number' && typeof srcValue === 'number') {
    return objValue + srcValue
  }
}

export function unsetById<T>(
  repository: { [key: string]: T },
  id: number
): { [key: string]: T } {
  return omit(repository, id)
}

export function observeStore<T, U>(
  store: Object,
  select: (state: T) => U,
  onChange: (current: U, next: U) => void
): () => void {
  let currentState

  function handleChange() {
    const nextState = select(store.getState())
    if (nextState !== currentState) {
      onChange(currentState, nextState)
      currentState = nextState
    }
  }

  const unsubscribe = store.subscribe(handleChange)
  handleChange()
  return unsubscribe
}

export function addVectors(a: Vector, b: Vector): Vector {
  return {
    x: a.x + b.x,
    y: a.y + b.y,
  }
}

export function subtractVectors(a: Vector, b: Vector): Vector {
  return {
    x: a.x - b.x,
    y: a.y - b.y,
  }
}

export function vectorLength(a: Vector): number {
  return Math.sqrt(Math.pow(a.x, 2) + Math.pow(a.y, 2))
}

export function divideVectorByScalar(a: Vector, b: number): Vector {
  return {
    x: a.x / b,
    y: a.y / b,
  }
}

export function normalizeVector(a: Vector): Vector {
  return divideVectorByScalar(a, vectorLength(a))
}

export function parseIntOr(value: any, or: number): number {
  const maybeInt = parseInt(value, 10)
  if (isNaN(maybeInt)) {
    return or
  }

  return maybeInt
}

export function getEventPositionRelativeToComponent(
  event: Object,
  component: Object
): { x: number, y: number } {
  const node: ?Object = ReactDOM.findDOMNode(component)

  if (node && node.getBoundingClientRect) {
    const bbox = node.getBoundingClientRect()

    return {
      x: event.clientX - bbox.left,
      y: event.clientY - bbox.top,
    }
  } else {
    throw new Error('Component could not be found')
  }
}

/*
 * returns a function that can be used to start a mouse drag.
 * calls callback for all events during a mouse drag (down, move..., up).
 * listens for mousemove and mouseup on the entire window.
 * coordinates registering and removing event listeners with the window.
 */
export function mouseDragHelper(
  callback: (event: Object) => void
): (event: Object) => void {
  return mouseDownEvent => {
    if (mouseDownEvent.type !== 'mousedown') {
      throw new Error(`
        callback returned by mouseDragHelper must
        be called with mousedown event
        and was instead called with ${mouseDownEvent.type} event
      `)
    }

    callback(mouseDownEvent)

    const handleMouseMove = mouseMoveEvent => {
      callback(mouseMoveEvent)
    }

    const handleMouseUp = mouseUpEvent => {
      callback(mouseUpEvent)
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)
  }
}

/*
 * we throw these to satisfy flow as flow is not able
 * to understand how sagas work
 */
export class SagaMalfunctionError extends Error {
  constructor() {
    const message = `
      this is a malfunction of redux-saga
      and should never happen.
    `
    super(message)
    this.message = message
    this.name = 'SagaMalfunctionError'
  }
}

export class ActionRequiresPropertyError extends Error {
  constructor(action: string, ...properties: Array<string>) {
    const formattedProperties = properties
      .map(prop => {
        return `\`${prop}\``
      })
      .join(', ')
    const message = `action \`${action}\` requires properties: ${formattedProperties}`
    super(message)
    this.message = message
    this.name = 'ActionRequiresPropertyError'
  }
}

export class EmbedError extends Error {
  constructor(
    into: { id: any, name: string },
    embed: { id: any, name: string }
  ) {
    const message = `
      when embedding associated \`${embed.name}\`
      into \`${into.name}\` with id \`${into.id}\`:
      expected to find \`${embed.name}\` with id \`${embed.id}\`
      in store but did not find it.
      THIS IS A BUG!
      it could mean that you have added some \`${into.name}\`
      to the store but missed adding associated \`${embed.name}\`
      before that.
      it could also mean that you removed some \`${embed.name}\`
      but missed removing associated \`${into.name}\` before that.
    `
    super(message)
    this.message = message
    this.name = 'EmbedError'
  }
}

export class BadResponseError extends Error {
  constructor(response: Object) {
    const message = `
      did not expect server to respond
      with status code ${response.status}
      and don't know how to handle it.
    `
    super(message)
    this.message = message
    this.name = 'BadResponseError'
  }
}

export class AlertDangerError extends Error {
  constructor(message: string) {
    super(message)
    this.message = message
    this.name = 'AlertDangerError'
  }
}

export class UnauthorizedError extends Error {
  constructor() {
    const message = `
      you don't have permission to see this page or do this action.
      please log in with an account that has the required permissions
      and try again.
    `
    super(message)
    this.message = message
    this.name = 'UnauthorizedError'
  }
}

export class NotFoundError extends Error {
  constructor(message: string = 'Not Found') {
    super(message)
    this.message = message
    this.name = 'NotFoundError'
  }
}

/*
 * if `value` is present in `array` and `shouldBePresent == false` then
 * a copy of array with `value` removed is returned.
 * if `value` is not present in `array` and `shouldBePresent == true` then
 * a copy of array with `value` added to the back is returned.
 * otherwise `array` is returned unchanged.
 */
export function ensurePresenceInArray<T>(
  array: Array<T>,
  value: T,
  shouldBePresent: boolean
): Array<T> {
  const isPresent = includes(array, value)
  if (isPresent && !shouldBePresent) {
    return without(array, value)
  } else if (!isPresent && shouldBePresent) {
    return concat(array, [value])
  }

  return array
}

export function* sagaHandleUnauthorized(): Generator<*, void, *> {
  const currentUser = yield select(selectCurrentUser)
  if (isToolUser(currentUser)) {
    return goToLogin()
  } else {
    console.log('sagaHandleUnauthorized, redirecting to login')
    yield put(goToOldLogin())
  }
  yield put(setAlertDangerNotAllowed())
}

export function* sagaHandleError(error: Error): Generator<*, void, *> {
  if (error.name === UnauthorizedError.name) {
    yield call(sagaHandleUnauthorized)
  } else if (error.name === AlertDangerError.name) {
    yield put(setAlertDanger(error.message))
  } else if (error.name === BadResponseError.name) {
    yield put(setAlertDanger(error.message))
  } else {
    throw error
  }
}

export function clickHandlerToEventHandler(
  clickHandler: (...args: Array<any>) => void,
  ...args: Array<any>
) {
  return () => {
    clickHandler(...args)
  }
}

export function changeHandlerToEventHandler(
  changeHandler: (value: any, ...args: Array<any>) => void,
  ...args: Array<any>
): (event: Event) => void {
  return (event: Event) => {
    if (event.target.value == null) {
      throw new Error('expected event.target.value to exist')
    }
    changeHandler(event.target.value, ...args)
  }
}

export function newMinLengthValidator(
  maxLength: number,
  item?: string
): (value: ?string) => ?string {
  return value => {
    if ('string' === typeof value && value.length < maxLength) {
      return `${item ? item + ' must' : 'Must'} have a value`
    }
    return null
  }
}

export function pollVerticalValidator(
  poll: Object,
  customerVerticals: Array<Object>,
  enableVerticalTargeting?: boolean
): ?string {
  if (!enableVerticalTargeting) return null

  if (!poll.addToAutomationLibrary || !customerVerticals.length) return null

  if (poll.verticals && poll.verticals.length) return null

  return 'Must choose at least one'
}

export function newMaxLengthValidator(
  maxLength: number,
  item?: string
): (value: ?string) => ?string {
  return value => {
    if ('string' === typeof value && value.length > maxLength) {
      return `${
        item ? item + ' must' : 'Must'
      } not be longer than ${maxLength} characters`
    }
    return null
  }
}

export function errorToValidationState(error: ?any): string {
  if (error != null) {
    return 'error'
  }
  return 'success'
}

export function customerSort(a: Object, b: Object) {
  return a.value === b.value ? 0 : a.value < b.value ? -1 : 1
}

export const localStorageSetItem = (key: string, data: Object) =>
  localStorage.setItem(key, JSON.stringify(data))

export const localStorageRemoveItem = (key: string) =>
  localStorage.removeItem(key)

export const localStorageGetItem = (key: string) => {
  const item = localStorage.getItem(key)
  return item ? JSON.parse(item) : null
}

export const sessionStorageSetItem = (key: string, data: Object) =>
  sessionStorage.setItem(key, JSON.stringify(data))

export const sessionStorageRemoveItem = (key: string) =>
  sessionStorage.removeItem(key)

export const sessionStorageGetItem = (key: string) => {
  const item = sessionStorage.getItem(key)
  return item ? JSON.parse(item) : null
}

// Polyfill for HTMLCanvasElement toBlob() taken from https://developer.mozilla.org/en-US/docs/Web/API/HTMLCanvasElement/toBlob
export const polyfillToBlob = () => {
  if (!HTMLCanvasElement.prototype.toBlob) {
    // $FlowFixMe
    Object.defineProperty(HTMLCanvasElement.prototype, 'toBlob', {
      value: function(callback, type, quality) {
        const binStr = atob(this.toDataURL(type, quality).split(',')[1]),
          len = binStr.length,
          arr = new Uint8Array(len)

        for (let i = 0; i < len; i++) {
          arr[i] = binStr.charCodeAt(i)
        }

        callback(new Blob([arr], { type: type || 'image/png' }))
      },
    })
  }
}

export const getCookie = (name: string) => {
  const cookieKey = `${name}=`
  const cookies = window.document.cookie.split(';')

  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].trim()
    if (cookie.indexOf(cookieKey) === 0)
      return cookie.substring(cookieKey.length, cookie.length)
  }

  return ''
}

export const isAuthenticated = () => {
  const hasAuthToken = !!sessionStorageGetItem(AUTH_TOKEN)
  const hasAuthCookie = !!getCookie(AUTH_COOKIE)

  return hasAuthToken || hasAuthCookie
}



// WEBPACK FOOTER //
// ./src/helpers.js